import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ShoppingCart, User, LogOut, Shield, DollarSign, Home, TrendingUp, 
  Package, Users, Mail, BarChart3, PieChart, RefreshCw, ArrowUp, ArrowDown,
  Target, Edit2, Check, X, Calendar, Trophy, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

export default function AdminAnalytics() {
  const { logout, getAuthHeader } = useAuth();
  const { itemCount } = useCart();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  // How many rows to show in the three "top" lists. Defaults to 5, expand to all.
  const [productsLimit, setProductsLimit] = useState(5);
  const [brandsLimit, setBrandsLimit] = useState(5);
  const [editingGoals, setEditingGoals] = useState(false);
  const [goalForm, setGoalForm] = useState({
    weekly_revenue: 1000,
    weekly_orders: 10,
    monthly_revenue: 4000,
    monthly_orders: 40
  });
  
  // Date range state
  const [dateRange, setDateRange] = useState('7d'); // 7d, 14d, 30d, custom
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [rangeData, setRangeData] = useState(null);
  
  // Debug modal state
  const [debugModal, setDebugModal] = useState(null); // 'uncategorized' | 'brands' | 'revenue' | null
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  
  // Top Spenders and Trends state
  const [topSpenders, setTopSpenders] = useState(null);
  const [trends, setTrends] = useState(null);
  const [loadingSpenders, setLoadingSpenders] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);

  const fetchDebugData = async (type) => {
    setDebugLoading(true);
    setDebugModal(type);
    try {
      const endpoints = {
        uncategorized: '/admin/debug/uncategorized-items',
        brands: '/admin/debug/brand-duplicates',
        revenue: '/admin/debug/daily-revenue'
      };
      const response = await axios.get(`${API}${endpoints[type]}`, {
        headers: getAuthHeader()
      });
      setDebugData(response.data);
    } catch (error) {
      console.error('Debug fetch error:', error);
      toast.error('Failed to fetch debug data');
      setDebugModal(null);
    } finally {
      setDebugLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/admin/analytics`, {
        headers: getAuthHeader()
      });
      setAnalytics(response.data);
      if (response.data.sales_goals) {
        setGoalForm(response.data.sales_goals);
      }
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  const fetchRangeData = async (start, end) => {
    try {
      const params = new URLSearchParams();
      if (start) params.append('start_date', start);
      if (end) params.append('end_date', end);
      
      const response = await axios.get(`${API}/admin/analytics/range?${params}`, {
        headers: getAuthHeader()
      });
      setRangeData(response.data);
    } catch (error) {
      console.error('Failed to fetch range data:', error);
    }
  };
  
  const handleDateRangeChange = (range) => {
    setDateRange(range);
    const today = new Date();
    let start, end;
    
    // Subtract (days - 1) to get correct range (e.g., 7d = today and 6 days before = 7 days total)
    switch (range) {
      case '7d':
        start = new Date(today - 6 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case '14d':
        start = new Date(today - 13 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case '30d':
        start = new Date(today - 29 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      case '90d':
        start = new Date(today - 89 * 24 * 60 * 60 * 1000);
        end = today;
        break;
      default:
        return;
    }
    
    const formatDate = (d) => d.toISOString().split('T')[0];
    fetchRangeData(formatDate(start), formatDate(end));
  };

  const saveGoals = async () => {
    try {
      await axios.put(`${API}/admin/sales-goals`, goalForm, {
        headers: getAuthHeader()
      });
      toast.success('Sales goals updated');
      setEditingGoals(false);
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to save goals');
    }
  };
  
  const fetchTopSpenders = async () => {
    try {
      setLoadingSpenders(true);
      const response = await axios.get(`${API}/admin/analytics/top-spenders?limit=10`, {
        headers: getAuthHeader()
      });
      setTopSpenders(response.data);
    } catch (error) {
      console.error('Failed to fetch top spenders:', error);
    } finally {
      setLoadingSpenders(false);
    }
  };
  
  const fetchTrends = async () => {
    try {
      setLoadingTrends(true);
      const response = await axios.get(`${API}/admin/analytics/trends`, {
        headers: getAuthHeader()
      });
      setTrends(response.data);
    } catch (error) {
      console.error('Failed to fetch trends:', error);
    } finally {
      setLoadingTrends(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    handleDateRangeChange('7d');
    fetchTopSpenders();
    fetchTrends();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono cursor-blink">LOADING ANALYTICS</div>
      </div>
    );
  }

  // Use range data for chart if available, otherwise use default 7-day data
  const chartData = rangeData?.daily_revenue || analytics?.daily_revenue || [];
  const maxDailyRevenue = Math.max(...(chartData.map(d => d.revenue) || [1]), 1);

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/catalog" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
            <Badge className="bg-tiffany-500 text-black rounded-none border-0">ADMIN</Badge>
          </Link>
          
          <nav className="flex items-center gap-4">
            <Link to="/catalog" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <Home className="w-5 h-5" />
            </Link>
            <Link to="/cart" className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <ShoppingCart className="w-5 h-5" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </Link>
            <Link to="/dashboard" className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors">
              <User className="w-5 h-5" />
            </Link>
            <Link to="/admin" className="p-2 text-tiffany-500">
              <Shield className="w-5 h-5" />
            </Link>
            <button onClick={logout} className="p-2 text-zinc-400 hover:text-red-500 transition-colors">
              <LogOut className="w-5 h-5" />
            </button>
          </nav>
        </div>
      </header>

      <main className="pt-24 pb-12 px-4 md:px-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm">
              ← BACK TO ADMIN
            </Link>
          </div>
          <Button
            onClick={fetchAnalytics}
            disabled={refreshing}
            className="bg-zinc-800 hover:bg-zinc-700 text-white rounded-none font-mono text-xs"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            REFRESH
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-8">
          <BarChart3 className="w-8 h-8 text-tiffany-500" />
          <h1 className="font-sans text-3xl text-white tracking-tighter">
            ANALYTICS DASHBOARD
          </h1>
        </div>

        {/* Revenue Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="border border-green-500/30 bg-green-500/5 p-4 md:p-6">
            <DollarSign className="w-6 h-6 text-green-500 mb-2" />
            <p className="font-mono text-2xl md:text-3xl text-green-500">
              ${analytics?.revenue?.total?.toFixed(2) || '0.00'}
            </p>
            <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">TOTAL REVENUE</p>
          </div>
          <div className="border border-tiffany-500/30 bg-tiffany-500/5 p-4 md:p-6">
            <TrendingUp className="w-6 h-6 text-tiffany-500 mb-2" />
            <p className="font-mono text-2xl md:text-3xl text-tiffany-500">
              ${analytics?.revenue?.month?.toFixed(2) || '0.00'}
            </p>
            <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">LAST 30 DAYS</p>
          </div>
          <div className="border border-blue-500/30 bg-blue-500/5 p-4 md:p-6">
            <DollarSign className="w-6 h-6 text-blue-500 mb-2" />
            <p className="font-mono text-2xl md:text-3xl text-blue-500">
              ${analytics?.revenue?.week?.toFixed(2) || '0.00'}
            </p>
            <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">LAST 7 DAYS</p>
          </div>
          <div className="border border-yellow-500/30 bg-yellow-500/5 p-4 md:p-6">
            <DollarSign className="w-6 h-6 text-yellow-500 mb-2" />
            <p className="font-mono text-2xl md:text-3xl text-yellow-500">
              ${analytics?.revenue?.today?.toFixed(2) || '0.00'}
            </p>
            <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">TODAY</p>
          </div>
        </div>

        {/* Profit Cards */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-mono text-lg text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              PROFIT TRACKING
            </h2>
            {analytics?.profit?.items_without_cost > 0 && (
              <Link 
                to="/admin?tab=products" 
                className="font-mono text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                onClick={() => {
                  // Store filter preference
                  sessionStorage.setItem('product_cost_filter', 'missing');
                }}
              >
                {analytics.profit.items_without_cost} ITEMS MISSING COST →
              </Link>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-emerald-500/30 bg-emerald-500/5 p-4 md:p-6">
              <DollarSign className="w-6 h-6 text-emerald-500 mb-2" />
              <p className="font-mono text-2xl md:text-3xl text-emerald-500">
                ${analytics?.profit?.total?.toFixed(2) || '0.00'}
              </p>
              <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">TOTAL PROFIT</p>
            </div>
            <div className="border border-emerald-400/30 bg-emerald-400/5 p-4 md:p-6">
              <TrendingUp className="w-6 h-6 text-emerald-400 mb-2" />
              <p className="font-mono text-2xl md:text-3xl text-emerald-400">
                ${analytics?.profit?.month?.toFixed(2) || '0.00'}
              </p>
              <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">LAST 30 DAYS</p>
            </div>
            <div className="border border-emerald-300/30 bg-emerald-300/5 p-4 md:p-6">
              <DollarSign className="w-6 h-6 text-emerald-300 mb-2" />
              <p className="font-mono text-2xl md:text-3xl text-emerald-300">
                ${analytics?.profit?.week?.toFixed(2) || '0.00'}
              </p>
              <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">LAST 7 DAYS</p>
            </div>
            <div className="border border-emerald-200/30 bg-emerald-200/5 p-4 md:p-6">
              <DollarSign className="w-6 h-6 text-emerald-200 mb-2" />
              <p className="font-mono text-2xl md:text-3xl text-emerald-200">
                ${analytics?.profit?.today?.toFixed(2) || '0.00'}
              </p>
              <p className="font-mono text-[10px] md:text-xs text-zinc-500 mt-1">TODAY</p>
            </div>
          </div>
          {analytics?.profit?.items_with_cost > 0 && (
            <p className="font-mono text-xs text-zinc-500 mt-2">
              BASED ON {analytics.profit.items_with_cost} ITEMS WITH COST DATA
            </p>
          )}
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="border border-zinc-800 p-4 text-center">
            <Package className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
            <p className="font-mono text-xl text-white">{analytics?.orders?.confirmed || 0}</p>
            <p className="font-mono text-[10px] text-zinc-500">CONFIRMED ORDERS</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <DollarSign className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
            <p className="font-mono text-xl text-white">${analytics?.revenue?.average_order?.toFixed(2) || '0.00'}</p>
            <p className="font-mono text-[10px] text-zinc-500">AVG ORDER VALUE</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <Users className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
            <p className="font-mono text-xl text-white">{analytics?.users?.total || 0}</p>
            <p className="font-mono text-[10px] text-zinc-500">TOTAL USERS</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <Mail className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
            <p className="font-mono text-xl text-white">{analytics?.mailing_list || 0}</p>
            <p className="font-mono text-[10px] text-zinc-500">MAILING LIST</p>
          </div>
          <div className="border border-zinc-800 p-4 text-center">
            <Package className="w-5 h-5 text-zinc-400 mx-auto mb-2" />
            <p className="font-mono text-xl text-white">
              {analytics?.inventory?.online || 0}/{analytics?.inventory?.total_products || 0}
            </p>
            <p className="font-mono text-[10px] text-zinc-500">PRODUCTS ONLINE</p>
          </div>
        </div>

        {/* Records Section */}
        <div className="border border-zinc-800 p-4 mb-8">
          <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
            <Trophy className="w-4 h-4 text-yellow-500" />
            ALL-TIME RECORDS
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">RECORD DAY</p>
              {analytics?.record_day ? (
                <>
                  <p className="font-mono text-2xl text-green-500">${analytics.record_day.revenue.toFixed(2)}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{analytics.record_day.date}</p>
                  <p className="font-mono text-[10px] text-zinc-600">{analytics.record_day.orders} orders</p>
                </>
              ) : (
                <p className="font-mono text-xl text-zinc-600">--</p>
              )}
            </div>
            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">RECORD WEEK</p>
              {analytics?.record_week ? (
                <>
                  <p className="font-mono text-2xl text-green-500">${analytics.record_week.revenue.toFixed(2)}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{analytics.record_week.start_date} - {analytics.record_week.end_date}</p>
                  <p className="font-mono text-[10px] text-zinc-600">{analytics.record_week.orders} orders</p>
                </>
              ) : (
                <p className="font-mono text-xl text-zinc-600">--</p>
              )}
            </div>
            <div className="bg-zinc-900/50 p-4 border border-zinc-800">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">RECORD MONTH</p>
              {analytics?.record_month ? (
                <>
                  <p className="font-mono text-2xl text-green-500">${analytics.record_month.revenue.toFixed(2)}</p>
                  <p className="font-mono text-[10px] text-zinc-500">{analytics.record_month.month}</p>
                  <p className="font-mono text-[10px] text-zinc-600">{analytics.record_month.orders} orders</p>
                </>
              ) : (
                <p className="font-mono text-xl text-zinc-600">--</p>
              )}
            </div>
            <div className="bg-zinc-900/50 p-4 border border-yellow-500/30">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">YEAR TO DATE</p>
              <p className="font-mono text-2xl text-yellow-500">${(analytics?.revenue?.ytd || 0).toFixed(2)}</p>
              <p className="font-mono text-[10px] text-zinc-500">{new Date().getFullYear()}</p>
              <p className="font-mono text-[10px] text-zinc-600">{analytics?.orders?.ytd || 0} orders</p>
            </div>
          </div>
        </div>

        {/* Date Range Selector */}
        <div className="flex items-center gap-2 mb-6">
          <Calendar className="w-4 h-4 text-zinc-500" />
          <div className="flex gap-1">
            {['7d', '14d', '30d', '90d'].map((range) => (
              <button
                key={range}
                onClick={() => handleDateRangeChange(range)}
                className={`px-3 py-1 font-mono text-xs transition-colors ${
                  dateRange === range 
                    ? 'bg-tiffany-500 text-black' 
                    : 'bg-zinc-900 text-zinc-400 hover:text-white border border-zinc-800'
                }`}
              >
                {range.toUpperCase()}
              </button>
            ))}
          </div>
          {rangeData && (
            <span className="font-mono text-[10px] text-zinc-500 ml-2">
              {rangeData.date_range?.start} to {rangeData.date_range?.end}
            </span>
          )}
        </div>

        {/* Range Summary */}
        {rangeData && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="border border-zinc-800 p-4">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">PERIOD REVENUE</p>
              <p className="font-mono text-2xl text-tiffany-500">${rangeData.summary?.total_revenue?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="border border-zinc-800 p-4">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">PERIOD ORDERS</p>
              <p className="font-mono text-2xl text-white">{rangeData.summary?.total_orders || 0}</p>
            </div>
            <div className="border border-zinc-800 p-4">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">AVG ORDER VALUE</p>
              <p className="font-mono text-2xl text-white">${rangeData.summary?.avg_order_value?.toFixed(2) || '0.00'}</p>
            </div>
            <div className="border border-zinc-800 p-4">
              <p className="font-mono text-[10px] text-zinc-500 mb-1">UNIQUE CUSTOMERS</p>
              <p className="font-mono text-2xl text-white">{rangeData.summary?.unique_customers || 0}</p>
            </div>
          </div>
        )}

        {/* Weekly Summary Stats - Always show fixed last 7 days, independent of period filter */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          <div className="border border-zinc-800 p-4">
            <p className="font-mono text-[10px] text-zinc-500 mb-1">7-DAY REVENUE</p>
            <p className="font-mono text-2xl text-tiffany-500">
              ${(analytics?.weekly_summary?.total_revenue || 0).toFixed(2)}
            </p>
            {analytics?.sales_goals?.weekly_revenue > 0 && (
              <div className="mt-2">
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-tiffany-500 transition-all"
                    style={{ width: `${Math.min(((analytics?.weekly_summary?.total_revenue || 0) / analytics?.sales_goals?.weekly_revenue) * 100, 100)}%` }}
                  />
                </div>
                <p className="font-mono text-[9px] text-zinc-600 mt-1">
                  {Math.round(((analytics?.weekly_summary?.total_revenue || 0) / analytics?.sales_goals?.weekly_revenue) * 100)}% of ${analytics?.sales_goals?.weekly_revenue} goal
                </p>
              </div>
            )}
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="font-mono text-[10px] text-zinc-500 mb-1">7-DAY ORDERS</p>
            <p className="font-mono text-2xl text-white">
              {analytics?.weekly_summary?.total_orders || 0}
            </p>
            {analytics?.sales_goals?.weekly_orders > 0 && (
              <div className="mt-2">
                <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-white transition-all"
                    style={{ width: `${Math.min(((analytics?.weekly_summary?.total_orders || 0) / analytics?.sales_goals?.weekly_orders) * 100, 100)}%` }}
                  />
                </div>
                <p className="font-mono text-[9px] text-zinc-600 mt-1">
                  {Math.round(((analytics?.weekly_summary?.total_orders || 0) / analytics?.sales_goals?.weekly_orders) * 100)}% of {analytics?.sales_goals?.weekly_orders} goal
                </p>
              </div>
            )}
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="font-mono text-[10px] text-zinc-500 mb-1">AVG DAILY REVENUE</p>
            <p className="font-mono text-2xl text-white">${analytics?.weekly_summary?.avg_daily_revenue?.toFixed(2) || '0.00'}</p>
          </div>
          <div className="border border-zinc-800 p-4">
            <p className="font-mono text-[10px] text-zinc-500 mb-1">RECORD SALES DAY</p>
            {analytics?.record_day ? (
              <>
                <p className="font-mono text-xl text-green-500">${analytics.record_day.revenue.toFixed(2)}</p>
                <p className="font-mono text-[10px] text-zinc-500">{analytics.record_day.date} ({analytics.record_day.orders} orders)</p>
              </>
            ) : (
              <p className="font-mono text-xl text-zinc-600">--</p>
            )}
          </div>
        </div>

        {/* Sales Goals Editor */}
        <div className="border border-zinc-800 p-4 mb-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-mono text-sm text-zinc-400 flex items-center gap-2">
              <Target className="w-4 h-4" />
              SALES GOALS
            </h3>
            {!editingGoals ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditingGoals(true)}
                className="h-7 px-2 rounded-none border-zinc-700 text-zinc-400 hover:text-white font-mono text-xs"
              >
                <Edit2 className="w-3 h-3 mr-1" /> EDIT
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={saveGoals}
                  className="h-7 px-2 rounded-none bg-green-500/20 text-green-400 hover:bg-green-500/30 font-mono text-xs"
                >
                  <Check className="w-3 h-3 mr-1" /> SAVE
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setEditingGoals(false);
                    setGoalForm(analytics?.sales_goals || {});
                  }}
                  className="h-7 px-2 rounded-none border-zinc-700 text-zinc-400 font-mono text-xs"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            )}
          </div>
          
          {editingGoals ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">WEEKLY REVENUE</label>
                <Input
                  type="number"
                  value={goalForm.weekly_revenue}
                  onChange={(e) => setGoalForm({...goalForm, weekly_revenue: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-sm"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">WEEKLY ORDERS</label>
                <Input
                  type="number"
                  value={goalForm.weekly_orders}
                  onChange={(e) => setGoalForm({...goalForm, weekly_orders: parseInt(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-sm"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">MONTHLY REVENUE</label>
                <Input
                  type="number"
                  value={goalForm.monthly_revenue}
                  onChange={(e) => setGoalForm({...goalForm, monthly_revenue: parseFloat(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-sm"
                />
              </div>
              <div>
                <label className="font-mono text-[10px] text-zinc-500 block mb-1">MONTHLY ORDERS</label>
                <Input
                  type="number"
                  value={goalForm.monthly_orders}
                  onChange={(e) => setGoalForm({...goalForm, monthly_orders: parseInt(e.target.value) || 0})}
                  className="bg-zinc-900 border-zinc-700 text-white rounded-none h-8 font-mono text-sm"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
              <div>
                <p className="font-mono text-lg text-white">${goalForm.weekly_revenue}</p>
                <p className="font-mono text-[10px] text-zinc-500">WEEKLY REV GOAL</p>
              </div>
              <div>
                <p className="font-mono text-lg text-white">{goalForm.weekly_orders}</p>
                <p className="font-mono text-[10px] text-zinc-500">WEEKLY ORDERS GOAL</p>
              </div>
              <div>
                <p className="font-mono text-lg text-white">${goalForm.monthly_revenue}</p>
                <p className="font-mono text-[10px] text-zinc-500">MONTHLY REV GOAL</p>
              </div>
              <div>
                <p className="font-mono text-lg text-white">{goalForm.monthly_orders}</p>
                <p className="font-mono text-[10px] text-zinc-500">MONTHLY ORDERS GOAL</p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Daily Revenue Chart */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              REVENUE BY DAY
            </h3>
            <div className="flex items-end justify-between gap-1 overflow-x-auto" style={{ height: '180px', minWidth: chartData.length > 14 ? `${chartData.length * 40}px` : 'auto' }}>
              {chartData.map((day, idx) => {
                const barHeight = maxDailyRevenue > 0 
                  ? Math.max((day.revenue / maxDailyRevenue) * 100, 4) 
                  : 4;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full min-w-[35px]">
                    {/* Revenue label above bar */}
                    <p className="font-mono text-[9px] text-tiffany-500 mb-1">
                      ${day.revenue > 0 ? day.revenue.toFixed(0) : '0'}
                    </p>
                    {/* Order count */}
                    <p className="font-mono text-[8px] text-zinc-500 mb-1">
                      {day.orders} ord
                    </p>
                    <div 
                      className="w-full bg-tiffany-500 relative group cursor-pointer"
                      style={{ height: `${barHeight}px` }}
                    >
                      {/* Hover tooltip with more details */}
                      <div className="absolute -top-16 left-1/2 -translate-x-1/2 bg-zinc-900 border border-zinc-700 px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                        <p className="font-mono text-[10px] text-tiffany-500">${day.revenue.toFixed(2)}</p>
                        <p className="font-mono text-[10px] text-zinc-400">{day.orders} orders</p>
                      </div>
                    </div>
                    <p className="font-mono text-[8px] text-zinc-500 mt-2 text-center">{day.date}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Order Status Breakdown */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              ORDER STATUS BREAKDOWN
            </h3>
            <div className="space-y-3">
              {Object.entries(analytics?.orders?.by_status || {}).map(([status, count]) => {
                const statusColors = {
                  pending: 'bg-yellow-500',
                  pending_fulfillment: 'bg-purple-500',
                  pending_shipment: 'bg-tiffany-500',
                  shipped: 'bg-blue-500',
                  out_for_delivery: 'bg-orange-500',
                  delivered: 'bg-green-500',
                  cancelled: 'bg-red-500',
                  expired: 'bg-zinc-500'
                };
                const total = analytics?.orders?.total || 1;
                const percentage = ((count / total) * 100).toFixed(0);
                return (
                  <div key={status} className="flex items-center gap-3">
                    <div className={`w-3 h-3 ${statusColors[status] || 'bg-zinc-500'}`} />
                    <div className="flex-1">
                      <div className="flex justify-between text-xs font-mono mb-1">
                        <span className="text-zinc-400 uppercase">{status.replace('_', ' ')}</span>
                        <span className="text-white">{count} ({percentage}%)</span>
                      </div>
                      <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${statusColors[status] || 'bg-zinc-500'}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Products */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <Package className="w-4 h-4" />
              TOP SELLING PRODUCTS
            </h3>
            <div className="space-y-3">
              {analytics?.top_products?.slice(0, productsLimit).map((product, idx) => (
                <div key={idx} className="flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-mono text-xs text-white truncate uppercase">{product.name}</p>
                    {product.brand && (
                      <p className="font-mono text-[10px] text-tiffany-500 uppercase">{product.brand}</p>
                    )}
                  </div>
                  <div className="flex items-start gap-4 shrink-0">
                    {typeof product.profit === 'number' && (
                      <div className="text-right">
                        <p className={`font-mono text-xs ${product.profit >= 0 ? 'text-tiffany-500' : 'text-red-500'}`} data-testid={`product-profit-${idx}`}>
                          ${product.profit.toFixed(0)}
                        </p>
                        <p className="font-mono text-[10px] text-zinc-500">profit</p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="font-mono text-xs text-green-500">${product.revenue.toFixed(0)}</p>
                      <p className="font-mono text-[10px] text-zinc-500">{product.quantity} sold</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!analytics?.top_products || analytics.top_products.length === 0) && (
                <p className="font-mono text-xs text-zinc-500 text-center py-4">No sales data yet</p>
              )}
              {analytics?.top_products?.length > 5 && (
                <button
                  onClick={() => setProductsLimit(productsLimit >= analytics.top_products.length ? 5 : analytics.top_products.length)}
                  className="w-full pt-3 border-t border-zinc-800 font-mono text-[10px] text-zinc-500 hover:text-tiffany-500 tracking-widest transition-colors"
                  data-testid="expand-products-btn"
                >
                  {productsLimit >= analytics.top_products.length
                    ? `COLLAPSE`
                    : `SHOW ALL (${analytics.top_products.length}) ▾`}
                </button>
              )}
            </div>
          </div>

          {/* Top Brands */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              TOP BRANDS
            </h3>
            <div className="space-y-3">
              {analytics?.top_brands?.slice(0, brandsLimit).map((brand, idx) => (
                <div key={idx} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="font-mono text-xs text-zinc-500">{idx + 1}.</span>
                    <span className="font-mono text-xs text-white uppercase truncate">{brand.name}</span>
                  </div>
                  <div className="flex items-start gap-4 shrink-0">
                    {typeof brand.profit === 'number' && (
                      <div className="text-right">
                        <p className={`font-mono text-xs ${brand.profit >= 0 ? 'text-tiffany-500' : 'text-red-500'}`} data-testid={`brand-profit-${idx}`}>
                          ${brand.profit.toFixed(0)}
                        </p>
                        <p className="font-mono text-[10px] text-zinc-500">profit</p>
                      </div>
                    )}
                    <div className="text-right">
                      <p className="font-mono text-xs text-green-500">${brand.revenue.toFixed(0)}</p>
                      <p className="font-mono text-[10px] text-zinc-500">{brand.quantity} items</p>
                    </div>
                  </div>
                </div>
              ))}
              {(!analytics?.top_brands || analytics.top_brands.length === 0) && (
                <p className="font-mono text-xs text-zinc-500 text-center py-4">No brand data yet</p>
              )}
              {analytics?.top_brands?.length > 5 && (
                <button
                  onClick={() => setBrandsLimit(brandsLimit >= analytics.top_brands.length ? 5 : analytics.top_brands.length)}
                  className="w-full pt-3 border-t border-zinc-800 font-mono text-[10px] text-zinc-500 hover:text-tiffany-500 tracking-widest transition-colors"
                  data-testid="expand-brands-btn"
                >
                  {brandsLimit >= analytics.top_brands.length
                    ? `COLLAPSE`
                    : `SHOW ALL (${analytics.top_brands.length}) ▾`}
                </button>
              )}
            </div>
          </div>

          {/* Category Breakdown */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <PieChart className="w-4 h-4" />
              CATEGORY BREAKDOWN
            </h3>
            <div className="space-y-3">
              {analytics?.category_breakdown?.map((cat, idx) => {
                const colors = ['bg-tiffany-500', 'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-red-500'];
                return (
                  <div key={idx} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <div className={`w-2 h-2 shrink-0 ${colors[idx % colors.length]}`} />
                      <span className="font-mono text-xs text-white uppercase truncate">{cat.name}</span>
                    </div>
                    <div className="flex items-start gap-4 shrink-0">
                      {typeof cat.profit === 'number' && (
                        <div className="text-right">
                          <p className={`font-mono text-xs ${cat.profit >= 0 ? 'text-tiffany-500' : 'text-red-500'}`} data-testid={`category-profit-${idx}`}>
                            ${cat.profit.toFixed(0)}
                          </p>
                          <p className="font-mono text-[10px] text-zinc-500">profit</p>
                        </div>
                      )}
                      <div className="text-right">
                        <p className="font-mono text-xs text-green-500">${cat.revenue.toFixed(0)}</p>
                        <p className="font-mono text-[10px] text-zinc-500">{cat.quantity} items</p>
                      </div>
                    </div>
                  </div>
                );
              })}
              {(!analytics?.category_breakdown || analytics.category_breakdown.length === 0) && (
                <p className="font-mono text-xs text-zinc-500 text-center py-4">No category data yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Sales Trends Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Link to Top Customers Page */}
          <Link 
            to="/admin/customers" 
            className="border border-zinc-800 p-6 hover:border-yellow-500/50 transition-colors group"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-mono text-sm text-zinc-400 flex items-center gap-2 group-hover:text-yellow-500 transition-colors">
                <Trophy className="w-4 h-4 text-yellow-500" />
                TOP CUSTOMERS
              </h3>
              <span className="font-mono text-[10px] text-tiffany-500 group-hover:text-tiffany-400">
                VIEW ALL →
              </span>
            </div>
            
            {loadingSpenders ? (
              <p className="text-zinc-500 font-mono text-xs text-center py-4">Loading...</p>
            ) : topSpenders?.top_spenders?.length > 0 ? (
              <div className="space-y-2">
                {topSpenders.top_spenders.slice(0, 3).map((spender, idx) => (
                  <div 
                    key={idx} 
                    className={`flex items-center gap-3 p-2 border ${
                      idx === 0 ? 'border-yellow-500/50 bg-yellow-500/5' :
                      idx === 1 ? 'border-zinc-400/50 bg-zinc-400/5' :
                      'border-amber-600/50 bg-amber-600/5'
                    }`}
                  >
                    <div className={`w-6 h-6 flex items-center justify-center font-mono text-xs font-bold ${
                      idx === 0 ? 'bg-yellow-500 text-black' :
                      idx === 1 ? 'bg-zinc-400 text-black' :
                      'bg-amber-600 text-black'
                    }`}>
                      {idx + 1}
                    </div>
                    <span className="font-mono text-xs text-white uppercase flex-1 truncate">
                      {spender.name}
                    </span>
                    <span className="font-mono text-xs text-green-500 font-bold">
                      ${spender.total_spent.toLocaleString()}
                    </span>
                  </div>
                ))}
                <p className="font-mono text-[10px] text-zinc-500 text-center pt-2">
                  + {(topSpenders.summary?.total_customers ?? topSpenders.top_spenders.length) - 3} more customers
                </p>
              </div>
            ) : (
              <p className="font-mono text-xs text-zinc-500 text-center py-4">No customer data yet</p>
            )}
          </Link>

          {/* Sales Trends Panel */}
          <div className="border border-zinc-800 p-6">
            <h3 className="font-mono text-sm text-zinc-400 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-tiffany-500" />
              SALES TRENDS
            </h3>
            
            {loadingTrends ? (
              <p className="text-zinc-500 font-mono text-xs text-center py-8">Loading...</p>
            ) : trends ? (
              <div className="space-y-6">
                {/* Best Day Insight */}
                {trends.insights?.best_day && (
                  <div className="bg-green-500/10 border border-green-500/30 p-3">
                    <p className="font-mono text-xs text-green-500">
                      🔥 HOTTEST DAY: <span className="font-bold">{trends.insights.best_day.full_name}</span>
                    </p>
                    <p className="font-mono text-[10px] text-zinc-400 mt-1">
                      ${trends.insights.best_day.revenue.toLocaleString()} revenue • {trends.insights.best_day.orders} orders
                    </p>
                  </div>
                )}
                
                {/* Day of Week Chart */}
                <div>
                  <p className="font-mono text-xs text-zinc-500 mb-3">BY DAY OF WEEK</p>
                  <div className="flex items-end gap-1 h-24">
                    {trends.days_of_week?.map((day, idx) => {
                      const maxRevenue = Math.max(...trends.days_of_week.map(d => d.revenue)) || 1;
                      const height = (day.revenue / maxRevenue) * 100;
                      const isBest = trends.insights?.best_day?.name === day.name;
                      
                      return (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                          <div 
                            className={`w-full transition-all ${
                              isBest ? 'bg-green-500' : 'bg-tiffany-500/60'
                            }`}
                            style={{ height: `${Math.max(height, 4)}%` }}
                            title={`$${day.revenue.toLocaleString()} - ${day.orders} orders`}
                          />
                          <span className={`font-mono text-[10px] ${
                            isBest ? 'text-green-500 font-bold' : 'text-zinc-500'
                          }`}>
                            {day.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Peak Hour */}
                {trends.insights?.peak_hour && (
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="font-mono text-xs text-zinc-500 mb-2">PEAK SHOPPING HOUR</p>
                    <div className="flex items-center gap-3">
                      <div className="bg-tiffany-500/20 px-3 py-2">
                        <p className="font-mono text-lg text-tiffany-500 font-bold">
                          {trends.insights.peak_hour.label}
                        </p>
                      </div>
                      <div>
                        <p className="font-mono text-xs text-white">
                          {trends.insights.peak_hour.orders} orders placed
                        </p>
                        <p className="font-mono text-[10px] text-zinc-500">
                          ${trends.insights.peak_hour.revenue.toLocaleString()} revenue
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Monthly Breakdown */}
                {trends.months_of_year && (
                  <div className="border-t border-zinc-800 pt-4">
                    <p className="font-mono text-xs text-zinc-500 mb-3">BY MONTH</p>
                    <div className="grid grid-cols-6 gap-1 text-center">
                      {trends.months_of_year.map((month, idx) => {
                        const maxMonthRev = Math.max(...trends.months_of_year.map(m => m.revenue)) || 1;
                        const intensity = month.revenue / maxMonthRev;
                        
                        return (
                          <div 
                            key={idx}
                            className="p-2"
                            style={{ 
                              backgroundColor: `rgba(45, 212, 191, ${intensity * 0.5})`,
                              border: '1px solid rgba(45, 212, 191, 0.2)'
                            }}
                            title={`${month.name}: $${month.revenue.toLocaleString()} (${month.orders} orders)`}
                          >
                            <p className="font-mono text-[10px] text-white">{month.name}</p>
                            <p className="font-mono text-[9px] text-zinc-400">${month.revenue > 1000 ? (month.revenue/1000).toFixed(1) + 'k' : month.revenue}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="font-mono text-xs text-zinc-500 text-center py-8">No trend data yet</p>
            )}
          </div>
        </div>

        {/* Debug Tools Section */}
        <div className="mt-8 border border-zinc-800 p-6">
          <h3 className="font-mono text-sm text-zinc-400 mb-4">DEBUG TOOLS</h3>
          <div className="flex flex-wrap gap-3">
            <button 
              onClick={() => fetchDebugData('uncategorized')}
              className="bg-yellow-500/20 text-yellow-500 px-3 py-2 font-mono text-xs hover:bg-yellow-500/30 transition-colors"
            >
              VIEW UNCATEGORIZED ITEMS
            </button>
            <button 
              onClick={() => fetchDebugData('brands')}
              className="bg-purple-500/20 text-purple-500 px-3 py-2 font-mono text-xs hover:bg-purple-500/30 transition-colors"
            >
              VIEW BRAND DUPLICATES
            </button>
            <button 
              onClick={() => fetchDebugData('revenue')}
              className="bg-tiffany-500/20 text-tiffany-500 px-3 py-2 font-mono text-xs hover:bg-tiffany-500/30 transition-colors"
            >
              DEBUG DAILY REVENUE
            </button>
          </div>
        </div>

        {/* Debug Modal */}
        {debugModal && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setDebugModal(null)}>
            <div className="bg-zinc-900 border border-zinc-700 max-w-2xl w-full max-h-[80vh] overflow-auto p-6" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-mono text-lg text-white">
                  {debugModal === 'uncategorized' && 'UNCATEGORIZED ITEMS'}
                  {debugModal === 'brands' && 'BRAND DUPLICATES'}
                  {debugModal === 'revenue' && 'DAILY REVENUE DEBUG'}
                </h3>
                <button onClick={() => setDebugModal(null)} className="text-zinc-400 hover:text-white text-2xl">&times;</button>
              </div>
              
              {debugLoading ? (
                <p className="text-zinc-400 font-mono text-sm">Loading...</p>
              ) : debugData ? (
                <div className="space-y-4">
                  {/* Uncategorized Items */}
                  {debugModal === 'uncategorized' && (
                    <>
                      <p className="font-mono text-sm text-yellow-500">Found {debugData.total_uncategorized} uncategorized item(s)</p>
                      {debugData.items?.map((item, idx) => (
                        <div key={idx} className="bg-zinc-800 p-3 border border-zinc-700">
                          <p className="font-mono text-sm text-white">{item.product_name}</p>
                          <p className="font-mono text-xs text-zinc-400">Product ID: {item.product_id}</p>
                          <p className="font-mono text-xs text-zinc-400">Order: {item.order_id} on {item.order_date}</p>
                          <p className="font-mono text-xs text-green-500">${item.item_total}</p>
                        </div>
                      ))}
                      {debugData.items?.length === 0 && (
                        <p className="text-zinc-500 font-mono text-sm">No uncategorized items found!</p>
                      )}
                    </>
                  )}
                  
                  {/* Brand Duplicates */}
                  {debugModal === 'brands' && (
                    <>
                      <p className="font-mono text-sm text-purple-500">
                        {debugData.brands_with_duplicates} brand(s) with case variations
                      </p>
                      {debugData.duplicates?.map((dup, idx) => (
                        <div key={idx} className="bg-zinc-800 p-3 border border-zinc-700">
                          <p className="font-mono text-sm text-white">Variations: {dup.variations.join(', ')}</p>
                          <p className="font-mono text-xs text-green-500">Combined: ${dup.combined_revenue} ({dup.combined_quantity} items)</p>
                        </div>
                      ))}
                      {debugData.duplicates?.length === 0 && (
                        <p className="text-zinc-500 font-mono text-sm">No brand duplicates found - all brands are normalized!</p>
                      )}
                    </>
                  )}
                  
                  {/* Daily Revenue */}
                  {debugModal === 'revenue' && (
                    <>
                      <p className="font-mono text-xs text-zinc-400">Server EST: {debugData.server_time_est}</p>
                      <p className="font-mono text-sm text-tiffany-500">{debugData.total_confirmed_orders} confirmed orders</p>
                      <div className="space-y-2 mt-4">
                        {debugData.days?.map((day, idx) => (
                          <div key={idx} className="bg-zinc-800 p-2 border border-zinc-700 flex justify-between">
                            <span className="font-mono text-xs text-white">{day.day_label}</span>
                            <span className="font-mono text-xs text-green-500">${day.total_revenue} ({day.order_count} orders)</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
